import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => {
    return articles.find((a) => a.slug === slug);
  }, [slug]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <header><Navbar /></header>
        <main className="pt-24 md:pt-28 px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center py-16">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-foreground-300">
              <i className="ri-file-close-line text-4xl" />
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-400 mb-6">该文章不存在或已被删除</p>
            <Link to="/" className="inline-block px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.category, href: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <header>
        <Navbar />
      </header>
      <main>
        {/* Article Header */}
        <section className="pt-24 md:pt-28 pb-6 md:pb-8 px-4 md:px-8 lg:px-12 bg-background-100 border-b border-background-200">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </section>

        {/* Article Content */}
        <article className="py-8 md:py-12 px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-block px-3 py-1 rounded-md bg-accent-100 text-accent-700 text-xs font-medium hover:bg-accent-200 transition-colors"
              >
                {article.category}
              </Link>
              {article.isHot && (
                <span className="inline-block px-3 py-1 rounded-md bg-primary-100 text-primary-700 text-xs font-medium">
                  热门
                </span>
              )}
              {article.isVideo && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs font-medium">
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-video-line" />
                  </span>
                  视频
                </span>
              )}
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-6 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 md:gap-6 text-sm text-foreground-400 mb-8 pb-8 border-b border-background-200">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-500 text-xs font-bold">
                  {article.author.charAt(0)}
                </div>
                <span className="font-medium text-foreground-600">{article.author}</span>
              </div>
              <span>{article.date}</span>
              <div className="flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-eye-line" />
                </span>
                <span>{article.views.toLocaleString()} 阅读</span>
              </div>
            </div>

            {/* Featured Image */}
            <div className="relative rounded-2xl overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full aspect-video object-cover"
                width="1200"
                height="675"
              />
              {article.isVideo && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-white/90 text-primary-500 hover:bg-white hover:scale-110 transition-all cursor-pointer">
                    <i className="ri-play-fill text-2xl md:text-3xl" />
                  </div>
                  <span className="absolute bottom-4 right-4 px-3 py-1 rounded-md bg-black/60 text-white text-sm font-medium">
                    {article.videoDuration}
                  </span>
                </div>
              )}
            </div>

            {/* Article Body */}
            <div className="prose prose-lg max-w-none">
              <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-6">
                {article.excerpt}
              </p>
              <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-6">
                据多位知情人士透露，此事件在圈内已经流传多时，但一直没有确凿证据。直到近日，有相关人员决定站出来揭露真相，才让这一事件逐渐浮出水面。
              </p>
              <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-6">
                记者经过多方求证，了解到事件背后的更多细节。相关人员表示，这种行为已经持续相当长的一段时间，影响范围也比外界想象的要广泛得多。
              </p>
              <blockquote className="border-l-4 border-primary-500 pl-4 md:pl-6 py-2 my-8 bg-background-100 rounded-r-lg">
                <p className="text-base md:text-lg text-foreground-700 italic leading-relaxed">
                  "我们只是想把真相告诉公众，让更多人了解事情的全貌。每一个细节都经过反复核实，确保信息的真实性。"
                </p>
                <cite className="text-sm text-foreground-400 not-italic mt-2 block">—— 知情人士透露</cite>
              </blockquote>
              <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-6">
                截至目前，事件相关方尚未对此事做出正式回应。但据悉，有关方面已经开始介入调查，后续进展我们将持续跟踪报道。
              </p>
              <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-6">
                91吃瓜网将持续关注此事的最新进展，为网友们带来第一手的资讯。请持续关注我们的后续报道。
              </p>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap items-center gap-2 mt-8 pt-8 border-t border-background-200">
              <span className="text-sm text-foreground-400 mr-2">标签：</span>
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/category/${article.categorySlug}`}
                  className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 text-xs font-medium hover:bg-primary-100 hover:text-primary-700 transition-colors"
                >
                  {tag}
                </Link>
              ))}
            </div>

            {/* Share */}
            <div className="flex items-center gap-3 mt-6">
              <span className="text-sm text-foreground-400">分享：</span>
              <button type="button" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-[#E6162D] hover:text-white transition-colors">
                <i className="ri-weibo-line" />
              </button>
              <button type="button" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-[#07C160] hover:text-white transition-colors">
                <i className="ri-wechat-line" />
              </button>
              <button type="button" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-[#12B7F5] hover:text-white transition-colors">
                <i className="ri-qq-line" />
              </button>
              <button type="button" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-foreground-800 hover:text-white transition-colors">
                <i className="ri-link" />
              </button>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-8 md:py-12 px-4 md:px-8 lg:px-12 bg-background-100 border-t border-background-200">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6">相关推荐</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <article
                    key={related.id}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200 hover:border-primary-200 transition-all duration-300"
                  >
                    <Link to={`/article/${related.slug}`} className="block">
                      <div className="relative aspect-video overflow-hidden">
                        <img
                          src={related.image}
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          width="400"
                          height="225"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {related.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-2 text-xs text-foreground-400">
                          <span>{related.date}</span>
                          <span className="flex items-center gap-1">
                            <span className="w-3 h-3 flex items-center justify-center">
                              <i className="ri-eye-line" />
                            </span>
                            {related.views.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}